/**
 * CMS-09 "Acceso" — grants by device, email or voucher (docs/06 §2.8, docs/07 §3).
 *
 * Phase 1 (docs/07 §1) every grant targets the single `plan_full` product, so the table shows
 * "Acceso completo" instead of a product column and neither price nor currency appears anywhere.
 * Everything else is server-side: the tab counters come with the page, so do the filters.
 */
import type {
  CmsGrantsQueryInput,
  CmsGrantsResponse,
  GrantDTO,
  GrantMatchType,
  GrantStatus,
} from '@akpl/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { api } from '../api';
import { atLeast, useAuth } from '../auth/AuthProvider';
import { GrantStatusBadge, MatchTypeBadge } from '../components/access/badges';
import { CsvImportDrawer } from '../components/access/CsvImportDrawer';
import { GrantDrawer } from '../components/access/GrantDrawer';
import { VoucherDrawer } from '../components/access/VoucherDrawer';
import { DataTable, PAGE_SIZE } from '../components/data-table/DataTable';
import { PageHeader } from '../components/layout/AppShell';
import { Button } from '../components/ui/button';
import { Input, Select } from '../components/ui/field';
import { useT } from '../i18n';
import { cn } from '../lib/cn';
import type { CsvColumn } from '../lib/csv';
import { fetchAllRowsResult } from '../lib/exportAll';
import { formatDateTime, formatNumber } from '../lib/format';

export interface GrantFilters {
  q: string;
  type: GrantMatchType | '';
  status: GrantStatus | '';
}

export const EMPTY_GRANT_FILTERS: GrantFilters = { q: '', type: '', status: '' };

const SORT_IDS = new Set(['match_value', 'status', 'valid_until', 'redemptions', 'created_at']);

export function toGrantsQuery(
  filters: GrantFilters,
  page: number,
  sorting: SortingState,
): CmsGrantsQueryInput {
  const query: CmsGrantsQueryInput = { page, pageSize: PAGE_SIZE };
  if (filters.q.trim()) query.q = filters.q.trim();
  if (filters.type) query.type = filters.type;
  if (filters.status) query.status = filters.status;
  const sort = sorting[0];
  if (sort && SORT_IDS.has(sort.id)) {
    query.sort = sort.id;
    query.dir = sort.desc ? 'desc' : 'asc';
  }
  return query;
}

const TABS: Array<{ key: GrantMatchType | ''; countKey: GrantMatchType | null }> = [
  { key: '', countKey: null },
  { key: 'device', countKey: 'device' },
  { key: 'email', countKey: 'email' },
  { key: 'voucher', countKey: 'voucher' },
];

const STATUSES: GrantStatus[] = ['pending', 'redeemed', 'expired', 'revoked'];

type OpenDrawer =
  { kind: 'grant'; grant: GrantDTO | 'new' } | { kind: 'csv' } | { kind: 'voucher' };

export function Access() {
  const { t, i18n } = useT();
  const locale = i18n.language;
  const { role } = useAuth();
  const canEdit = atLeast(role, 'editor');

  const [filters, setFilters] = useState<GrantFilters>(EMPTY_GRANT_FILTERS);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'created_at', desc: true }]);
  const [open, setOpen] = useState<OpenDrawer | null>(null);

  const query = toGrantsQuery(filters, page, sorting);
  const result = useQuery<CmsGrantsResponse>({
    queryKey: ['cms', 'grants', query],
    queryFn: () => api.cmsGrants({ query }),
    placeholderData: keepPreviousData,
  });

  /**
   * There is no count until the query answers. `?? { device: 0, … }` made the four filter chips
   * read "Todos 0 · Device 0 · Email 0 · Voucher 0" for the whole first load before settling on
   * 427 — an operator reads that as "the grants are gone" (QA 2026-09-15 UX-530). While the
   * counts are unknown the chips show an ellipsis instead.
   */
  const counts = result.data?.counts ?? null;
  const totalAll = counts ? counts.device + counts.email + counts.voucher : null;

  const update = (patch: Partial<GrantFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const columns = useMemo<ColumnDef<GrantDTO>[]>(
    () => [
      {
        id: 'match_value',
        header: t('cms:grants.columns.matchValue'),
        cell: ({ row }) => (
          <span className="flex items-center gap-2">
            <MatchTypeBadge type={row.original.matchType} />
            <span className="font-semibold break-all text-ink">{row.original.matchValue}</span>
          </span>
        ),
      },
      {
        id: 'access',
        header: t('cms:grants.columns.access'),
        cell: () => <span className="text-ink">{t('cms:grants.accessFull')}</span>,
      },
      {
        id: 'duration',
        header: t('cms:grants.columns.duration'),
        cell: ({ row }) =>
          row.original.durationDays === null
            ? t('cms:grants.perpetual')
            : t('cms:grants.days', { count: row.original.durationDays }),
      },
      {
        id: 'txType',
        header: t('cms:grants.columns.txType'),
        cell: ({ row }) => t(`cms:grants.tx.${row.original.txType}` as 'cms:grants.tx.manual'),
      },
      {
        id: 'status',
        header: t('cms:grants.columns.status'),
        cell: ({ row }) => <GrantStatusBadge status={row.original.status} />,
      },
      {
        id: 'redemptions',
        header: t('cms:grants.columns.redemptions'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.redemptions}/{row.original.maxRedemptions}
          </span>
        ),
      },
      {
        id: 'valid_until',
        header: t('cms:grants.columns.validUntil'),
        cell: ({ row }) =>
          row.original.validUntil
            ? formatDateTime(row.original.validUntil, locale)
            : t('cms:grants.never'),
      },
      {
        id: 'createdBy',
        header: t('cms:grants.columns.createdBy'),
        cell: ({ row }) => (
          <span className="text-muted">
            {row.original.createdByEmail ?? row.original.createdBy}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  const csvColumns: CsvColumn<GrantDTO>[] = [
    { header: 'id', value: (g) => g.id },
    { header: 'match_type', value: (g) => g.matchType },
    { header: 'match_value', value: (g) => g.matchValue },
    { header: 'duration_days', value: (g) => g.durationDays },
    { header: 'tx_type', value: (g) => g.txType },
    { header: 'external_ref', value: (g) => g.externalRef },
    { header: 'valid_until', value: (g) => g.validUntil },
    { header: 'status', value: (g) => g.status },
    { header: 'redemptions', value: (g) => g.redemptions },
    { header: 'created_by', value: (g) => g.createdByEmail ?? g.createdBy },
    { header: 'notes', value: (g) => g.notes },
  ];

  return (
    <>
      <PageHeader
        title={t('cms:grants.title')}
        subtitle={t('cms:grants.subtitle')}
        actions={
          canEdit ? (
            <span className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setOpen({ kind: 'csv' })}>
                {t('cms:grants.importCsv')}
              </Button>
              <Button variant="secondary" onClick={() => setOpen({ kind: 'voucher' })}>
                {t('cms:grants.generateVouchers')}
              </Button>
              <Button
                data-testid="grant-new"
                onClick={() => setOpen({ kind: 'grant', grant: 'new' })}
              >
                + {t('cms:grants.new')}
              </Button>
            </span>
          ) : null
        }
      />

      <div className="px-6 pb-8">
        <DataTable
          columns={columns}
          data={result.data?.items ?? []}
          getRowId={(row) => row.id}
          label={t('cms:grants.title')}
          total={result.data?.total ?? 0}
          page={page}
          onPageChange={setPage}
          sorting={sorting}
          onSortingChange={(next) => {
            setSorting(next);
            setPage(1);
          }}
          isLoading={result.isLoading}
          isFetching={result.isFetching}
          isError={result.isError}
          onRetry={() => void result.refetch()}
          onRowClick={(grant) => setOpen({ kind: 'grant', grant })}
          rowLabel={(grant) => t('cms:table.openRow', { name: grant.matchValue })}
          sortableIds={SORT_IDS}
          selectedRowId={open?.kind === 'grant' && open.grant !== 'new' ? open.grant.id : null}
          emptyLabel={t('cms:grants.empty')}
          csv={{
            filename: 'akapella-grants.csv',
            columns: csvColumns,
            // The whole filtered set, not the page on screen (CMS-22).
            rows: (onProgress) =>
              fetchAllRowsResult(
                (exportPage, pageSize) =>
                  api.cmsGrants({ query: { ...query, page: exportPage, pageSize } }),
                { onProgress },
              ),
          }}
          toolbar={
            <>
              <div
                role="tablist"
                aria-label={t('cms:grants.title')}
                className="flex items-center gap-1 rounded-[10px] border border-line bg-panel p-1"
              >
                {TABS.map((tab) => {
                  const active = filters.type === tab.key;
                  const count =
                    counts === null
                      ? null
                      : tab.countKey === null
                        ? totalAll
                        : counts[tab.countKey];
                  return (
                    <button
                      key={tab.key || 'all'}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      onClick={() => update({ type: tab.key })}
                      className={cn(
                        'flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-[13px]',
                        active ? 'bg-canvas font-semibold text-ink' : 'text-muted hover:text-ink',
                      )}
                    >
                      {t(`cms:grants.tabs.${tab.key || 'all'}` as 'cms:grants.tabs.all')}
                      <span className="text-[12px] text-muted tabular-nums">
                        {count === null ? '…' : formatNumber(count, locale)}
                      </span>
                    </button>
                  );
                })}
              </div>

              <Select
                className="w-44"
                aria-label={t('cms:grants.columns.status')}
                value={filters.status}
                onChange={(e) => update({ status: e.target.value as GrantStatus | '' })}
              >
                <option value="">{t('cms:grants.statusAll')}</option>
                {STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`cms:status.${status}` as 'cms:status.pending')}
                  </option>
                ))}
              </Select>

              <Input
                className="w-64"
                placeholder={t('cms:grants.searchPlaceholder')}
                aria-label={t('cms:grants.searchPlaceholder')}
                value={filters.q}
                onChange={(e) => update({ q: e.target.value })}
              />
              <Button variant="link" size="sm" onClick={() => update(EMPTY_GRANT_FILTERS)}>
                {t('cms:table.clearFilters')}
              </Button>
            </>
          }
        />
      </div>

      {open?.kind === 'grant' && (
        <GrantDrawer
          key={open.grant === 'new' ? 'new' : `${open.grant.id}:${open.grant.updatedAt}`}
          grant={open.grant}
          canEdit={canEdit}
          onClose={() => setOpen(null)}
        />
      )}
      {open?.kind === 'csv' && <CsvImportDrawer onClose={() => setOpen(null)} />}
      {open?.kind === 'voucher' && <VoucherDrawer onClose={() => setOpen(null)} />}
    </>
  );
}
